import React from 'react';

import { IFacialRecognition, TOnboardingType } from 'shared/domain/entities';
import { Animation } from 'shared/presentation/components/atoms';
import { Button } from 'shared/presentation/components/molecules';
import { ANIMATIONS, ICONS, TIconType } from 'shared/presentation/constants';
import { useLocale } from 'shared/presentation/contexts';

import alertAnimation from './assets/alert.json';
import * as S from './styles';

const ALERT_ANIMATION = {
  data: alertAnimation,
  height: 150,
  width: 150,
};

const ERROR_ANIMATION = {
  data: ANIMATIONS.ERROR,
  height: 150,
  width: 250,
};

const ONBOARDING_ICON_DICTIONARY: Record<TOnboardingType, TIconType> = {
  DOCUMENTOSCOPY: ICONS.DOCUMENT,
  FACIAL_BIOMETRY: ICONS.FACE_SCAN,
  ONE_FOR_ONE_BIOMETRY: ICONS.FACE_SCAN,
};

interface IFailedStatus {
  activeOnboarding: TOnboardingType;
  state: Extract<IFacialRecognition['state'], { type: 'FAILED' }>;
  availableOnboardings: TOnboardingType[];
  onGoToOnboarding(onboarding: TOnboardingType): void;
}

const FailedStatus: React.FC<IFailedStatus> = ({
  activeOnboarding,
  state,
  availableOnboardings,
  onGoToOnboarding,
}) => {
  const { t } = useLocale('shared');

  const canRetry = availableOnboardings.length;

  const animation = canRetry ? ALERT_ANIMATION : ERROR_ANIMATION;

  const title = canRetry
    ? t('We could not recognize you')
    : t('Ops something went wrong!');

  const ONBOARDING_BUTTON_TEXT_DICTIONARY: Record<TOnboardingType, string> = {
    DOCUMENTOSCOPY: t('Try with document'),
    FACIAL_BIOMETRY: t('Try with selfie'),
    ONE_FOR_ONE_BIOMETRY: t('Try with selfie'),
  };

  const hasOnlyOneContinueOption = availableOnboardings.length === 1;

  return (
    <S.Container>
      <main>
        <Animation animation={animation} loop />

        <section>
          <h1>{title}</h1>

          {state.message ? (
            <p>{state.message}</p>
          ) : (
            <>
              <p>
                {t(
                  'We could not validate your face Follow these instructions and try again',
                )}
              </p>

              <ul>
                <li>{t('Remove glasses or hats')}</li>
                <li>{t('Move to a well-lit place')}</li>
                <li>
                  {t(
                    'The background should be neutral For instance, stay in from of a bright wall',
                  )}
                </li>
                <li>
                  {t(
                    'Make sure your face is the only one appearing in the photo',
                  )}
                </li>
              </ul>
            </>
          )}
        </section>

        <footer>
          {availableOnboardings.map(onboarding => (
            <Button
              key={onboarding}
              icon={ONBOARDING_ICON_DICTIONARY[onboarding] || ICONS.REFRESH}
              variant={
                hasOnlyOneContinueOption || activeOnboarding !== onboarding
                  ? 'secondary'
                  : 'default'
              }
              onClick={() => onGoToOnboarding(onboarding)}
            >
              {ONBOARDING_BUTTON_TEXT_DICTIONARY[onboarding]}
            </Button>
          ))}
        </footer>
      </main>
    </S.Container>
  );
};

export default FailedStatus;
